import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Zap, AlertCircle } from 'lucide-react';
import { fetchHashRate, type HashRateData } from '@/data-access/blocks';

interface NetworkHashRateCardProps {
  className?: string;
}

const defaultHashRateData: HashRateData = {
  hashRate: 623.47,
  unit: "EH/s",
  timestamp: Date.now() / 1000
};

export function NetworkHashRateCard({ 
  className 
}: NetworkHashRateCardProps) {
  const { 
    data: hashRateData, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['network-hashrate'],
    queryFn: fetchHashRate,
    refetchInterval: 300000, // Refetch every 5 minutes (hash rate changes slowly)
    staleTime: 180000, // Consider data stale after 3 minutes
  });

  // Use real data if available, otherwise fall back to default
  const displayData = hashRateData || defaultHashRateData;
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/20">
            <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-foreground">Network Hash Rate</h3>
              {error && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs text-muted-foreground">Cached</span>
                </div>
              )}
              {!isLoading && !error && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-muted-foreground">Live</span>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">Mining Power</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="space-y-1">
            <Skeleton className="h-9 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        ) : (
          <div className="space-y-1">
            <p className="text-3xl font-bold text-foreground">
              {displayData.hashRate.toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })} <span className="text-2xl font-normal text-muted-foreground">{displayData.unit}</span>
            </p>
            <p className="text-sm text-muted-foreground">24h Network Average</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}